import React, { useEffect, useRef, useState } from "react";
import { getClassName } from "@utils";
import { InfoFilledIcon, InfoIcon } from "@taskpane/icons";
import { TooltipContent, TooltipContentProps } from "./components";
import css from "./Tooltip.module.scss";

export interface TooltipProps extends Omit<TooltipContentProps, "isVisible" | "parentRef"> {
  children?: React.ReactNode;
  contentClassName?: string;
  icon?: React.FC<React.SVGProps<SVGSVGElement>> | null;
  iconClassName?: string;
  delay?: number;
  disabled?: boolean;
}

const Tooltip: React.FC<TooltipProps> = ({
  children,
  className,
  contentClassName,
  icon,
  iconClassName,
  delay = 0,
  disabled = false,
  ...rest
}) => {
  const [isVisible, setIsVisible] = useState(false);

  const parentRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<number | null>(null);

  const IconDefaultComponent = icon === null ? null : icon || InfoIcon;
  const IconFilledComponent = icon === null ? null : icon || InfoFilledIcon;
  const IconComponent = isVisible ? IconFilledComponent : IconDefaultComponent;

  const attrs = {
    container: {
      className: getClassName(
        css.container,
        isVisible && "opened",
        disabled && "disabled",
        className
      ),
      ref: parentRef,
      onMouseEnter: () => {
        timeoutRef.current = window.setTimeout(() => {
          if (!disabled) {
            setIsVisible(true);
          }
        }, delay);
      },
      onMouseLeave: () => {
        if (typeof timeoutRef.current === "number") {
          window.clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }

        setIsVisible(false);
      },
    },
    icon: {
      className: getClassName(css.icon, iconClassName),
    },
    tooltipContent: {
      isVisible,
      parentRef,
      className: contentClassName,
      ...rest,
    },
  };

  useEffect(() => {
    const handler = () => {
      setIsVisible(false);
    };

    document.addEventListener("scroll", handler, true);

    return () => {
      document.removeEventListener("scroll", handler, true);
    };
  }, []);

  return (
    <div {...attrs.container}>
      {children}
      {!children && IconComponent ? <IconComponent {...attrs.icon} /> : null}
      <TooltipContent {...attrs.tooltipContent} />
    </div>
  );
};

export default Tooltip;
